<template>
  <div class="multi-alert" v-if="!isDismissed">
    <div class="rounded-md select-none" v-bind="$attrs" :class="[sizeClass, bgColor, $attrs.class]">
      <div class="flex items-center justify-between space-x-2" :class="spaceClass">
        <div class="flex grow items-center justify-between" :class="spaceClass">
          <div :class="textColor">
            <FontAwesomeIcon :icon="['fas', iconPath]" :size="iconSize" />
          </div>
          <div :class="[textColor]" class="w-full">
            <div :class="[titleSize]" class="font-medium" v-if="$slots.title">
              <slot name="title" />
            </div>
            <div :class="[bodySize]" v-if="$slots.default">
              <slot />
            </div>
          </div>
        </div>

        <div v-if="showTimeRemaining" :class="[textColor]">
          {{ formattedTime }}
        </div>
        <slot name="action" v-bind="{ buttonColor }">
          <GeneralButton
            v-if="closable"
            :color="buttonColor"
            size="large"
            @click="handleClose"
            :icon="['fas', 'xmark']"
            class="p-0!"
          />
        </slot>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue"
import { useStorage } from "@vueuse/core"
import { AlertTypes } from "@/interfaces/component/alert.interface"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import GeneralButton from "@/components/inputs/GeneralButton.vue"

const props = withDefaults(
  defineProps<{
    /**
     * Alert visual style
     */
    type?: AlertTypes

    /**
     * The alert's size
     */
    size?: "small" | "medium" | "large"

    /**
     * Whether the alert can be closed
     */
    closable?: boolean

    /**
     * The time in milliseconds to auto-close the alert
     */
    autoCloseMs?: number

    /**
     * Whether to show the time remaining
     */
    showTimeRemaining?: boolean

    /**
     * Storage key to persist dismissed state. If provided, the alert will be hidden when dismissed.
     */
    storageKey?: string
  }>(),
  {
    type: AlertTypes.NEUTRAL,
    size: "medium",
    showTimeRemaining: false
  }
)

const emit = defineEmits(["close"])

defineOptions({
  inheritAttrs: false
})

let closeTimeout: ReturnType<typeof setTimeout> | undefined
let countdownInterval: ReturnType<typeof setInterval> | undefined

const timeLeft = ref(props.autoCloseMs ?? 0)
const isDismissed = ref(false)

const formattedTime = computed(() => {
  return Math.ceil(timeLeft.value / 1000)
})

function handleClose() {
  if (props.storageKey) {
    const dismissedStorage = useStorage(`${props.storageKey}`, false)
    dismissedStorage.value = true
    isDismissed.value = true
  }
  emit("close")
}

function handleAutoClose() {
  emit("close")
}

onMounted(() => {
  // Check if alert was previously dismissed
  if (props.storageKey) {
    const dismissedStorage = useStorage(`${props.storageKey}`, false)
    isDismissed.value = dismissedStorage.value
  }

  if (props.autoCloseMs !== undefined) {
    // initialize and start the countdown interval
    timeLeft.value = props.autoCloseMs
    countdownInterval = setInterval(() => {
      timeLeft.value = Math.max(0, timeLeft.value - 1000)

      // stop the interval once it hits zero
      if (timeLeft.value === 0) {
        clearInterval(countdownInterval)
      }
    }, 1000)

    // existing auto‐close
    closeTimeout = setTimeout(() => {
      handleAutoClose()
    }, props.autoCloseMs)
  }
})

onUnmounted(() => {
  clearTimeout(closeTimeout)
  if (countdownInterval) {
    clearInterval(countdownInterval)
  }
})

const sizeClass = computed(() => {
  switch (props.size) {
    case "large":
      return "px-4 py-2"
    case "small":
      return "px-2 py-0"
    case "medium":
    default:
      return "px-3 py-1"
  }
})

const textColor = computed(() => {
  if (props.type === AlertTypes.SUCCESS) return "text-on-success"
  if (props.type === AlertTypes.ERROR) return "text-on-error"
  if (props.type === AlertTypes.WARNING) return "text-on-warning"
  return "text-on-info"
})

const buttonColor = computed(() => {
  if (props.type === AlertTypes.SUCCESS) return "success"
  if (props.type === AlertTypes.ERROR) return "error"
  if (props.type === AlertTypes.WARNING) return "warning"
  return "info"
})

const iconPath = computed(() => {
  if (props.type === AlertTypes.SUCCESS) return "circle-check"
  if (props.type === AlertTypes.ERROR) return "circle-xmark"
  if (props.type === AlertTypes.WARNING) return "triangle-exclamation"
  return "circle-info"
})

const iconSize = computed(() => {
  switch (props.size) {
    case "small":
      return "sm"
    case "large":
      return "xl"
    case "medium":
    default:
      return "lg"
  }
})

const bgColor = computed(() => {
  if (props.type === AlertTypes.SUCCESS) return "bg-success"
  if (props.type === AlertTypes.ERROR) return "bg-error"
  if (props.type === AlertTypes.WARNING) return "bg-warning"
  return "bg-info"
})

const titleSize = computed(() => {
  switch (props.size) {
    case "large":
      return "text-lg"
    case "medium":
      return "text-base"
    case "small":
      return "text-sm"
    default:
      return ""
  }
})

const bodySize = computed(() => {
  switch (props.size) {
    case "large":
      return "text-base"
    case "medium":
      return "text-sm"
    case "small":
      return "text-xs"
    default:
      return ""
  }
})

const spaceClass = computed(() => {
  switch (props.size) {
    case "large":
      return "space-x-4"
    case "medium":
      return "space-x-3"
    case "small":
      return "space-x-2"
    default:
      return "space-x-3"
  }
})
</script>

<style scoped></style>
